/**
 * Wrap bare http(s) URLs and scheme-less `generalmedicine.co/…` paths in `<a>` tags
 * so they render as clickable hyperlinks.
 *
 * URLs already inside an `<a>...</a>` are left untouched so authored markup
 * keeps its label and attributes. Generated anchors use `target="_blank"` and
 * `rel="noopener noreferrer"` so they open in a new tab without giving the
 * destination access to `window.opener`.
 *
 * Trailing punctuation that is almost never part of a URL (`.`, `,`, `;`,
 * `:`, `!`, `?`, closing quotes/brackets) is pulled back out of the match so
 * a URL written like `(https://example.com)` or `Visit https://example.com.`
 * does not swallow the surrounding character.
 */
const URL_PATTERN =
    '(?:https?:\\/\\/)' +
    '(?:' +
    'localhost|' +
    '(?:[a-z0-9]?(?:[a-z0-9\\-]{1,61}[a-z0-9])?\\.[^\\.|\\s])+[a-z\\.]*[a-z]+|' +
    '(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)' +
    '(?:\\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}' +
    ')' +
    '(?::\\d{1,5})?' +
    '(?:[/?#][^\\s<>"]*)?'

const TRAILING_PUNCTUATION = /[.,;:!?"'\]}>]+$/

const ANCHOR_ATTRIBUTES = 'target="_blank" rel="noopener noreferrer"'

/**
 * Scheme-less General Medicine URLs sent in SMS or chat (e.g. `generalmedicine.co/foo`).
 * Restricted to our apex/www host and a path so arbitrary domains and email addresses
 * are not linkified. The lookbehind avoids matching inside `https://…` or inside
 * generated `<a>` markup.
 */
const GENERAL_MEDICINE_BARE_URL_PATTERN =
    '(?:^|(?<![\\w.@:/">=\\-]))' + '(?:www\\.)?generalmedicine\\.co' + '\\/[^\\s<>"]*'

const trimTrailingNoise = (url: string): { url: string; trailing: string } => {
    let working = url
    let trailing = ''
    let changed = true

    // Loop because stripping one kind of trailing noise can expose more of the
    // other: e.g. `https://example.com).` ends with `.`, but after the period
    // is removed the unbalanced `)` should also be pulled out. Conversely
    // `https://example.com.)` exposes a trailing `.` after the paren is
    // dropped. Balanced parens like `Function_(mathematics)` stay inside the
    // URL because the open/close counts match.
    while (changed) {
        changed = false

        const punctuationMatch = working.match(TRAILING_PUNCTUATION)
        if (punctuationMatch) {
            trailing = punctuationMatch[0] + trailing
            working = working.slice(0, working.length - punctuationMatch[0].length)
            changed = true
        }

        while (working.endsWith(')')) {
            const opens = (working.match(/\(/g) || []).length
            const closes = (working.match(/\)/g) || []).length
            if (closes <= opens) break
            trailing = ')' + trailing
            working = working.slice(0, -1)
            changed = true
        }
    }

    return { url: working, trailing }
}

const wrapUrlAsAnchor = (href: string, label: string, trailing: string) =>
    `<a href="${href}" ${ANCHOR_ATTRIBUTES}>${label}</a>${trailing}`

const linkifyHttpUrls = (segment: string) =>
    segment.replace(new RegExp(URL_PATTERN, 'gi'), (match) => {
        const { url, trailing } = trimTrailingNoise(match)
        if (!url) {
            return match
        }
        return wrapUrlAsAnchor(url, url, trailing)
    })

const linkifyBareGeneralMedicineUrls = (segment: string) =>
    segment.replace(new RegExp(GENERAL_MEDICINE_BARE_URL_PATTERN, 'gi'), (match) => {
        const { url, trailing } = trimTrailingNoise(match)
        if (!url) {
            return match
        }
        const href = `https://${url}`
        return wrapUrlAsAnchor(href, url, trailing)
    })

const autolinkText = (text: string | null) => {
    if (!text) {
        return ''
    }

    try {
        const segments = text.split(/(<a[^>]*>.*?<\/a>)/gi)

        return segments
            .map((segment) => {
                if (segment.startsWith('<a') && segment.endsWith('</a>')) {
                    return segment
                }

                // linkifyHttpUrls can produce an anchor whose href/label embeds a second
                // generalmedicine.co path (e.g. a redirect URL's query string), so re-split
                // on its output before the second pass to keep it from nesting inside.
                return linkifyHttpUrls(segment)
                    .split(/(<a[^>]*>.*?<\/a>)/gi)
                    .map((inner) =>
                        inner.startsWith('<a') && inner.endsWith('</a>')
                            ? inner
                            : linkifyBareGeneralMedicineUrls(inner),
                    )
                    .join('')
            })
            .join('')
    } catch (e) {
        console.error(e)
        return text
    }
}

export default autolinkText
